import React, { useEffect, useRef, useState } from 'react';
import IntroductionPage from './IntroductionPage';
import FeatureGrid from './FeatureGrid';
import SystemFeatures from './SystemFeatures';
import Statistics from './Statistics';
import Partners from './Partners';
import Contact from './Contact';

const MainLayout = () => {
    const containerRef = useRef(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheel = (e) => {
            e.preventDefault();

            if (isScrolling) return;
            setIsScrolling(true);

            const direction = e.deltaY > 0 ? 1 : -1;
            const nextPage = Math.max(0, Math.min(5, currentPage + direction));

            setCurrentPage(nextPage);

            container.scrollTo({
                left: nextPage * window.innerWidth,
                behavior: 'smooth'
            });

            setTimeout(() => {
                setIsScrolling(false);
            }, 700);
        };

        const updateScrollProgress = () => {
            const maxScroll = container.scrollWidth - window.innerWidth;
            const currentScroll = container.scrollLeft;
            const progress = (currentScroll / maxScroll) * 100;
            setScrollProgress(Math.min(100, Math.max(0, progress)));
        };

        container.addEventListener('wheel', handleWheel);
        container.addEventListener('scroll', updateScrollProgress);

        return () => {
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('scroll', updateScrollProgress);
        };
    }, [currentPage, isScrolling]);


    const navigateToPage = (direction) => {
        const container = containerRef.current;
        if (container) {
            const pageWidth = window.innerWidth;
            const targetPage = currentPage + direction;
            container.scrollTo({
                left: targetPage * pageWidth,
                behavior: 'smooth'
            });
        }
    };

    const handleNavigation = (direction) => {
        if (isScrolling) return;
        setIsScrolling(true);

        const nextPage = Math.max(0, Math.min(4, currentPage + direction));
        setCurrentPage(nextPage);

        containerRef.current?.scrollTo({
            left: nextPage * window.innerWidth,
            behavior: 'smooth'
        });

        setTimeout(() => {
            setIsScrolling(false);
        }, 700);
    };

    // New function to handle logo click
    const handleLogoClick = () => {
        if (isScrolling) return;
        setIsScrolling(true);

        setCurrentPage(0);
        containerRef.current?.scrollTo({
            left: 0,
            behavior: 'smooth'
        });

        setTimeout(() => {
            setIsScrolling(false);
        }, 700);
    };


    return (
        <div
            ref={containerRef}
            className="h-screen w-screen overflow-x-auto overflow-y-hidden hide-scrollbar"
            style={{
                scrollBehavior: 'smooth',
                scrollSnapType: 'x mandatory',
                backgroundColor: '#1A1D21',
                backgroundImage: 'linear-gradient(var(--grid-color) 1px, transparent 1px), linear-gradient(90deg, var(--grid-color) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
                '--grid-color': 'rgba(47, 55, 65, 1)'
            }}
        >
            <div className="flex h-full">
                <header
                    className="fixed top-0 left-0 right-0 z-10 px-4 sm:px-8 md:px-16 py-4 md:py-6 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 shadow-md">
                    {/* Logo */}
                    <div className="flex items-center">
                        <img
                            src="/logo.svg"
                            alt="OpenGov"
                            className="h-12 sm:h-16 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={handleLogoClick}
                        />
                    </div>

                    {/* Contact Info */}
                    <div
                        className="flex flex-col sm:flex-row items-center gap-2 sm:gap-8 text-white text-sm sm:text-base text-center sm:text-left">
    <span>
      Имэйл: <a href="mailto:info@dataland.mn" className="text-yellow-400">info@dataland.mn</a>
    </span>
                        <span>
      Утас: <a href="tel:75077171" className="text-yellow-400">7507 7171</a>
    </span>
                    </div>
                </header>


                <div className="flex">
                    <div className="min-w-[100vw] h-full snap-start"><IntroductionPage/></div>
                    <div className="min-w-[100vw] h-full snap-start"><FeatureGrid/></div>
                    <div className="min-w-[100vw] h-full snap-start"><SystemFeatures/></div>
                    <div className="min-w-[100vw] h-full snap-start"><Statistics/></div>
                    <div className="min-w-[100vw] h-full snap-start"><Partners/></div>
                    {/*<div className="min-w-[100vw] h-full snap-start"><Contact/></div>*/}
                </div>

                {/* Scroll Indicator */}
                <div className="fixed left-8 bottom-8 w-[calc(100vw-4rem)] flex items-center justify-between  ">
                    {/* Scroll Indicator Group - 80% width */}
                    <div className="flex items-center gap-2 w-[90%] ">
                        {/* Text */}
                        <div className="flex items-center justify-center w-16 h-6 bg-yellow-400 rounded-full shadow-lg">
                            <span className="text-black text-sm font-medium">Scroll</span>
                        </div>
                        {/* Line */}
                        <div className="relative flex-1 "> {/* flex-1 to take remaining space */}
                            {/* Full line background */}
                            <div className="w-full h-1 bg-gray-800/50 rounded-full bg-[#2A2E35]">
                                {/* Progress line */}
                                <div
                                    className="absolute left-0 top-0 h-1 bg-yellow-400 rounded-full transition-all duration-300"
                                    style={{width: `${scrollProgress}%`}}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Copyright - 20% width */}
                    <div className="w-[20%] text-right pr-8">
                        <span className="text-yellow-400 font-medium whitespace-nowrap">
                            © {new Date().getFullYear()}
                        </span>
                    </div>
                </div>


                {/* Navigation Arrows */}
                {currentPage > 0 && (
                    <button
                        className="fixed left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                        onClick={() => handleNavigation(-1)}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 18l-6-6 6-6"/>
                        </svg>
                    </button>
                )}

                {/* Right Navigation Arrow */}
                {currentPage < 4 && (
                    <button
                        className="fixed right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                        onClick={() => handleNavigation(1)}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6"/>
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default MainLayout;